import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/layout/Logo";
import { Mail, Phone, MapPin, Clock, Truck, Instagram, Linkedin } from "lucide-react";

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0a0a0f] mt-12" data-testid="footer">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-8 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-10">

        {/* Brand column — spans 2 cols on large */}
        <div className="lg:col-span-2">
          <Logo size={50} />
          <p className="text-white/55 text-sm mt-5 max-w-xs leading-relaxed">
            Australia's one-stop solution for road freight transport. Reliable, affordable, and built around your urgency.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href="tel:+61470344648"
              className="inline-flex items-center gap-2 rounded-full border border-[#19e6c8]/30 bg-[#19e6c8]/5 hover:bg-[#19e6c8]/10 px-4 py-2 text-[#19e6c8] text-xs font-display tracking-wider transition-all"
            >
              <Phone size={13} /> +61 470 344 648
            </a>
          </div>

          {/* Acknowledgement of Country */}
          <div className="mt-6 max-w-xs flex flex-col items-center">
            <img
              src="/assets/acknowledgement-logo.png"
              alt="Aboriginal and Torres Strait Islander flags"
              className="h-12 w-auto"
            />
            <p className="text-white/55 text-sm mt-3 leading-relaxed text-left">
              We acknowledge the Traditional Aboriginal and Torres Strait Islander Owners of the land, sea and waters of the Australian continent and pay our respects to Elders past, present, and emerging.
            </p>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="brand-title text-xs text-white/85 mb-5 tracking-wider">QUICK LINKS</h4>
          <ul className="space-y-2.5 text-sm text-white/55">
            <li><Link to="/" className="hover:text-[#19e6c8] transition-colors">Home</Link></li>
            <li><Link to="/about" className="hover:text-[#19e6c8] transition-colors">About Us</Link></li>
            <li><Link to="/services" className="hover:text-[#19e6c8] transition-colors">Our Services</Link></li>
            <li><Link to="/track" className="hover:text-[#19e6c8] transition-colors">Track Shipment</Link></li>
            <li><Link to="/login" className="hover:text-[#19e6c8] transition-colors">Client Login</Link></li>
            <li><Link to="/terms" className="hover:text-[#19e6c8] transition-colors">Terms & Conditions</Link></li>
            <li><Link to="/privacy" className="hover:text-[#19e6c8] transition-colors">Privacy Policy</Link></li>
            <li><Link to="/sitemap" className="hover:text-[#19e6c8] transition-colors">Sitemap</Link></li>
          </ul>
        </div>

        {/* Services */}
        <div>
          <h4 className="brand-title text-xs text-white/85 mb-5 tracking-wider">SERVICES</h4>
          <ul className="space-y-2.5 text-sm text-white/55">
            <li className="hover:text-white/80 transition-colors cursor-default">Standard / General</li>
            <li className="hover:text-white/80 transition-colors cursor-default">Permanent Hire</li>
            <li className="hover:text-white/80 transition-colors cursor-default">LCL / Airfreight</li>
            <li className="hover:text-white/80 transition-colors cursor-default">FCL</li>
            <li className="hover:text-white/80 transition-colors cursor-default">Interstate B-double</li>
            <li className="hover:text-white/80 transition-colors cursor-default">Freight Forwarding Services</li>
            <li className="hover:text-white/80 transition-colors cursor-default">Same Day Express</li>
            <li className="hover:text-white/80 transition-colors cursor-default">VIP</li>
            <li className="hover:text-white/80 transition-colors cursor-default">Hourly / Custom</li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="brand-title text-xs text-white/85 mb-5 tracking-wider">CONTACT</h4>
          <ul className="space-y-3.5 text-sm text-white/55">
            <li className="flex items-start gap-2.5">
              <MapPin size={14} className="mt-0.5 text-[#19e6c8] shrink-0" />
              Brisbane, Queensland, Australia
            </li>
            <li className="flex items-center gap-2.5">
              <Phone size={14} className="text-[#19e6c8] shrink-0" />
              <a href="tel:+61470344648" className="hover:text-[#19e6c8] transition-colors">+61 470 344 648</a>
            </li>
            <li className="flex items-center gap-2.5">
              <Mail size={14} className="text-[#19e6c8] shrink-0" />
              <a href="mailto:hello@drivnfreight.com.au" className="hover:text-[#19e6c8] transition-colors break-all">
                hello@drivnfreight.com.au
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <Clock size={14} className="mt-0.5 text-[#19e6c8] shrink-0" />
              <span>
                Mon–Fri: 7:00 AM – 5:00 PM<br />
                Sat–Sun: By Appointment
              </span>
            </li>
          </ul>

          {/* Social links */}
          <div className="mt-5 flex items-center gap-3">
            <a
              href="https://www.instagram.com/drivnfreight"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-8 h-8 rounded-full border border-white/15 hover:border-[#e1306c]/60 hover:bg-[#e1306c]/10 flex items-center justify-center text-white/45 hover:text-[#e1306c] transition-all"
            >
              <Instagram size={14} />
            </a>
            <a
              href="https://www.linkedin.com/company/drivnfreight"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="w-8 h-8 rounded-full border border-white/15 hover:border-[#0a66c2]/60 hover:bg-[#0a66c2]/10 flex items-center justify-center text-white/45 hover:text-[#0a66c2] transition-all"
            >
              <Linkedin size={14} />
            </a>
          </div>

          {/* Driver Portal */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <p className="text-white/35 text-xs font-display tracking-wider mb-3">DRIVER PORTAL</p>
            <Link
              to="/driver-login"
              className="inline-flex items-center gap-2 text-xs text-[#f59e0b]/70 hover:text-[#f59e0b] font-display tracking-wider transition-colors"
            >
              <Truck size={12} /> Driver Login
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 py-5 px-6 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-4 text-white/35 text-xs">
        <div className="flex flex-col items-center sm:items-start gap-2 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center">
            <span>© {new Date().getFullYear()} DRIVN FREIGHT PTY LTD · Australia · All rights reserved.</span>
            <span className="flex items-center gap-4">
              <Link to="/terms" className="hover:text-white/70 transition-colors underline underline-offset-2" data-testid="footer-terms-btn">
                Terms &amp; Conditions
              </Link>
              <Link to="/privacy" className="hover:text-white/70 transition-colors underline underline-offset-2" data-testid="footer-privacy-btn">
                Privacy Policy
              </Link>
            </span>
          </div>
          <span data-testid="footer-abn">
            ABN: 41 658 624 385 - Also trading as{" "}
            <a
              href="https://drivnteq.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/70 transition-colors underline underline-offset-2"
              data-testid="footer-abn-link"
            >
              DRIVN TEQ
            </a>
            .
          </span>
        </div>
        <a
          href="https://drivnteq.com.au"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm hover:text-white/70 transition-colors"
        >
          <span className="mr-1">Made By</span>
          <img src="/assets/drivnfright.png" alt="DRIVNTEQ" className="h-5 w-auto" />
          <span className="tracking-wider">DRIVN<span className="font-bold">TEQ</span></span>
        </a>
      </div>
    </footer>
  );
}
